// Enhanced ResultsDisplay.tsx with new API response format handling

import React, { useState, useEffect } from 'react';
import { Pin, PinOff, ChevronRight, Copy, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { SimulationResult, PlayerResult } from '../types/types';
import { usePinnedPlayers } from '../hooks/usePinnedPlayers';
import { formatLargeNumber } from '../utils/formatUtils';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { Button } from './ui/button';
import Pagination from './Pagination';
import InfoTooltip from './InfoTooltip';
import { useSwipePagination } from '../hooks/useSwipePagination';
import { usePageTransition } from '../hooks/usePageTransition';

const PAGINATION_THRESHOLD = 20;

// Inner component for the results table with swipe support
const ResultsTable: React.FC<{
  allPlayers: PlayerResultWithElimination[];
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  renderResultRow: (player: PlayerResultWithElimination, globalIndex: number) => React.ReactNode;
}> = ({ allPlayers, currentPage, onPageChange, pageSize, renderResultRow }) => {
  const showPagination = allPlayers.length > PAGINATION_THRESHOLD;
  const totalPages = showPagination ? Math.ceil(allPlayers.length / pageSize) : 1;
  const items = showPagination
    ? allPlayers.slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize)
    : allPlayers;
  const globalOffset = showPagination ? (currentPage - 1) * pageSize : 0;
  const { handlePageChange, slideClass } = usePageTransition(onPageChange, currentPage);
  const swipeHandlers = useSwipePagination(currentPage, totalPages, handlePageChange);

  return (
    <div className="mb-4 relative overflow-x-auto" {...(showPagination ? swipeHandlers : {})}>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-9 text-right pr-2 px-1">#</TableHead>
            <TableHead className="text-left px-1.5">
              Player
              <InfoTooltip content={
                <div>
                  <p>Click on a player's name to see their rooting interests</p>
                </div>
              } />
            </TableHead>
            <TableHead className="text-center px-1">
              Paths
              <InfoTooltip content={
                <div>
                  <p>The number of possible outcomes that result in a win for this player</p>
                </div>
              } />
            </TableHead>
            <TableHead className="text-center px-1">
              Prob
              <InfoTooltip content={
                <div>
                  <p>A weighted analysis of how likely each of the remaining outcomes are</p>
                </div>
              } />
            </TableHead>
            <TableHead className="w-7 px-0.5"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className={slideClass}>
          {allPlayers.length > 0 ? (
            items.map((player, index) => {
              const globalIndex = globalOffset + index;
              return renderResultRow(player, globalIndex);
            })
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground">
                No competitive players available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {showPagination && (
        <Pagination
          totalItems={allPlayers.length}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          pageSize={pageSize}
        />
      )}
    </div>
  );
};

// Extended interface to handle new API response format
interface PlayerResultWithElimination extends PlayerResult {
  eliminationStatus?: 'ALIVE' | 'ELIMINATED';
  probabilityPercentage?: number; // New field from API
  winPercentage?: number; // Legacy field from API (no longer used)
}

interface ResultsDisplayProps {
  results: SimulationResult | null;
  onPlayerSelect: (playerName: string) => void;
  bracketComplete?: boolean;
  pinnedPlayers?: string[];
  onPinnedPlayersChange?: (players: string[]) => void;
  pageSize?: number;
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({
  results,
  onPlayerSelect,
  bracketComplete = false,
  pinnedPlayers = [],
  onPinnedPlayersChange,
  pageSize = 15
}) => {
  const [showEliminated, setShowEliminated] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [copied, setCopied] = useState(false);
  const { togglePin, pinToast, MAX_PINS } = usePinnedPlayers(pinnedPlayers, onPinnedPlayersChange);

  // Reset to page 1 when results change
  useEffect(() => {
    setCurrentPage(1);
  }, [results]);

  if (!results) return null;

  const players = Array.isArray(results.players) ? results.players : [];
  const { totalPossibleScenarios } = results;

  // Categorize and sort players by probability percentage
  const competitivePlayers: PlayerResultWithElimination[] = [];
  const eliminatedPlayers: PlayerResultWithElimination[] = [];

  players.forEach((player: PlayerResultWithElimination) => {
    // Set eliminationStatus if not provided by API
    if (!player.eliminationStatus) {
      // If no eliminationStatus AND probabilityPercentage is 0, treat as eliminated
      if ((player.probabilityPercentage || 0) === 0.0) {
        player.eliminationStatus = 'ELIMINATED';
      } else {
        player.eliminationStatus = 'ALIVE';
      }
    }

    // Categorize players based on elimination status
    if (player.eliminationStatus === 'ELIMINATED') {
      eliminatedPlayers.push(player);
    } else {
      competitivePlayers.push(player);
    }
  });

  // Sort competitive players by probability percentage (descending)
  competitivePlayers.sort((a, b) => {
    const aProbability = a.probabilityPercentage || 0;
    const bProbability = b.probabilityPercentage || 0;
    return bProbability - aProbability;
  });

  // Sort eliminated players alphabetically
  eliminatedPlayers.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Combine into single list: competitive then eliminated
  const allPlayers = [...competitivePlayers, ...eliminatedPlayers];

  // Compute rank map before pagination slicing
  const rankMap = new Map<string, number>();
  let rank = 1;
  for (let i = 0; i < allPlayers.length; i++) {
    const player = allPlayers[i];
    if (player.eliminationStatus === 'ELIMINATED') {
      rankMap.set(player.name, competitivePlayers.length + 1);
    } else {
      if (i > 0 && (player.probabilityPercentage || 0) < (allPlayers[i - 1].probabilityPercentage || 0)) {
        rank = i + 1;
      }
      rankMap.set(player.name, rank);
    }
  }

  // Enhanced formatting functions for new API format
  const formatPercentage = (player: PlayerResultWithElimination): string => {
    const { probabilityPercentage, eliminationStatus } = player;
    const probability = probabilityPercentage || 0;

    // Case 1: ALIVE players with 0.0 probability should show < 0.1%
    if (eliminationStatus === 'ALIVE' && probability === 0.0) {
      return '< 0.1%';
    }

    // Case 2: Very small non-zero probabilities that round to 0
    if (probability > 0 && Math.round(probability) === 0) {
      return '< 1%';
    }

    // Case 3: Normal percentage (round to 0 decimal places)
    return `${Math.round(probability)}%`;
  };

  const formatPaths = (player: PlayerResultWithElimination): string => {
    const { wins } = player;

    // Handle zero wins case - show threshold as 0.01% of total scenarios
    if (wins === 0) {
      const threshold = (Math.floor(totalPossibleScenarios * 0.0001 / 100))*100; // 0.01% threshold but obfuscated and rounded via /100 * 100
      if (threshold < 4) {
        return '< 3';
      }
      return `< ${formatLargeNumber(threshold)}`;
    }

    // Use existing formatLargeNumber function for all win counts
    return formatLargeNumber(wins);
  };

  const isPlayerClickable = (player: PlayerResultWithElimination): boolean => {
    // Not clickable if bracket is complete
    if (bracketComplete) return false;

    // Not clickable if 0 wins (too uncertain for rooting interests)
    if (player.wins === 0) return false;

    return true;
  };

  const handleCopyEliminated = () => {
    const text = eliminatedPlayers.map(p => p.name).join(', ');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Fail silently if clipboard API is unavailable
    });
  };

  // Determine if player is eliminated (for inline styling)
  const isEliminated = (player: PlayerResultWithElimination): boolean => {
    return player.eliminationStatus === 'ELIMINATED' && !bracketComplete;
  };

  // Pinned player data for the pinned section
  const pinnedPlayerData = allPlayers.filter(p => pinnedPlayers.includes(p.name));

  // Helper to render a result row (shared between pinned section and main table)
  const renderResultRow = (player: PlayerResultWithElimination, globalIndex: number, isPinnedSection: boolean = false) => {
    const isClickable = isPlayerClickable(player);
    const eliminated = isEliminated(player);
    const playerRank = rankMap.get(player.name) || globalIndex + 1;
    const isPinned = pinnedPlayers.includes(player.name);

    return (
      <TableRow
        key={`${isPinnedSection ? 'pin-' : ''}${player.name}`}
        className={cn(
          eliminated && "bg-muted/50"
        )}
      >
        <TableCell className={cn(
          "text-right pr-2 w-9 py-3 px-0.5",
          eliminated && "text-muted-foreground"
        )}>
          {playerRank}
        </TableCell>
        <TableCell
          className={cn(
            "max-w-[110px] truncate py-3 px-1.5 text-left",
            eliminated && "text-muted-foreground line-through cursor-default",
            !eliminated && isClickable && "text-primary cursor-pointer hover:underline",
            !eliminated && !isClickable && "text-muted-foreground cursor-default",
            isPinnedSection && "text-[13px]"
          )}
          title={player.name || `Player ${globalIndex + 1}`}
          onClick={() => isClickable && onPlayerSelect(player.name)}
        >
          {player.name || `Player ${globalIndex + 1}`}
        </TableCell>
        <TableCell className={cn(
          "text-center py-3 px-0.5",
          eliminated && "text-muted-foreground",
          isPinnedSection && "text-[13px]"
        )}>
          {eliminated ? '0' : formatPaths(player)}
        </TableCell>
        <TableCell className={cn(
          "text-center py-3 px-0.5",
          eliminated && "text-muted-foreground",
          isPinnedSection && "text-[13px]"
        )}>
          {eliminated ? '0%' : formatPercentage(player)}
        </TableCell>
        <TableCell className="w-7 text-center py-1 px-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => { e.stopPropagation(); togglePin(player.name); }}
            title={isPinned ? 'Unpin player' : 'Pin player'}
            disabled={!onPinnedPlayersChange}
          >
            {isPinned
              ? <Pin className="h-4 w-4 text-warning fill-warning" />
              : <PinOff className="h-4 w-4 text-muted-foreground" />
            }
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="my-5">
      {/* Pinned Players Section */}
      {pinnedPlayerData.length > 0 && (
        <div className="bg-primary/10 mb-2.5">
          <div className="text-xs font-semibold text-primary mb-1 px-1">Pinned Players</div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-9 text-right pr-2 px-1 h-8">#</TableHead>
                <TableHead className="text-left px-1.5 h-8">Player</TableHead>
                <TableHead className="text-center px-1 h-8">Paths</TableHead>
                <TableHead className="text-center px-1 h-8">Prob</TableHead>
                <TableHead className="w-7 px-0.5 h-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pinnedPlayerData.map((player, i) => renderResultRow(player, i, true))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pin limit toast */}
      {pinToast && (
        <div className="bg-warning/10 border border-warning/40 rounded-lg py-1.5 px-3 text-[13px] text-warning-foreground mb-2 text-center">
          Maximum of {MAX_PINS} pinned players. Unpin one to add another.
        </div>
      )}

      {/* Main Results Table */}
      <ResultsTable
        allPlayers={allPlayers}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        renderResultRow={renderResultRow}
      />

      {/* Eliminated Players Accordion (Copy Utility) */}
      {eliminatedPlayers.length > 0 && (
        <div className="mt-5 border-t border-border pt-4">
          <button
            onClick={() => setShowEliminated(!showEliminated)}
            className="bg-transparent border-none text-muted-foreground cursor-pointer flex items-center py-2 px-0 text-sm font-medium mb-2.5 hover:text-foreground transition-colors"
          >
            <ChevronRight className={cn(
              "h-3 w-3 mr-2 transition-transform duration-200",
              showEliminated && "rotate-90"
            )} />
            Eliminated ({eliminatedPlayers.length})
          </button>
          {showEliminated && (
            <div className="flex items-start gap-2">
              <span className="text-sm flex-1 my-1">
                {eliminatedPlayers.map(player => player.name || 'Unknown Player').join(', ')}
              </span>
              <Button
                variant="outline"
                size="icon"
                className={cn(
                  "h-7 w-7 shrink-0",
                  copied && "text-success border-success/40"
                )}
                onClick={handleCopyEliminated}
                title="Copy eliminated player names"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResultsDisplay;
